import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { useContext, useState, useEffect } from 'react';
import { AppContext } from 'context/app-context';
import { setModal } from 'store/features/modelSlice';
import { initModal } from 'helpers/utils';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import InputField from 'components/common/input-field';
import { ReactComponent as CrossIcon } from 'assets/icon/x-circle.svg';
import {
  createDevice,
  getDeviceProvidersList,
  updateDevice,
} from 'store/features/vehicleConfigSlice';
import toast from 'react-hot-toast';
import Select, { IDefaultOption } from 'components/common/select';
import { IDevice } from 'interfaces/vehicle-config-interfaces';
import { useTranslation } from 'react-i18next';

const initModelData: IDevice = { imeie: '', provider_details: null };
const AddEditDevice = () => {
  const dispatch = useAppDispatch();
  const {
    modal: {
      content: { title, formFor, callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const { deviceProviders, selectedDevice } = useAppSelector(state => state.vehicleConfig);
  const { setLoader } = useContext(AppContext);
  const [deviceData, setDeviceData] = useState<IDevice>(
    formFor === 'EDIT' && selectedDevice ? selectedDevice : initModelData,
  );
  const [deviceProviderOptions, setDeviceProviderOptions] = useState<IDefaultOption[]>([]);
  const [error, setError] = useState<{ device: string; deviceProvider: string } | null>();

  const { t } = useTranslation();
  const handleAddDevice = async () => {
    try {
      setLoader(true);
      await dispatch(
        createDevice({
          imeie: deviceData?.imeie,
          provider_id: deviceData?.provider_details?._id || '',
        }),
      )
        .then(() => {
          callBackFun && callBackFun();
          toast.success(t('workshopAppointment.successDeviceCreateToastMessage'));
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleEditDevice = async () => {
    try {
      setLoader(true);

      await dispatch(
        updateDevice({
          device_id: deviceData?._id || '',
          request: {
            imeie: deviceData?.imeie,
            provider_id: deviceData?.provider_details?._id || '',
          },
        }),
      )
        .then(() => {
          callBackFun && callBackFun();
          toast.success(t('workshopAppointment.successDeviceUpdateToastMessage'));
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleSubmit = () => {
    try {
      if (!deviceData?.provider_details?._id || !deviceData?.imeie?.trim()) {
        setError({
          ...error,
          deviceProvider: !deviceData?.provider_details?._id
            ? t('workshopAppointment.errorDeviceToasterMessage')
            : '',
          device: !deviceData?.imeie?.trim()
            ? t('workshopAppointment.errorIMEIToasterMessage')
            : '',
        });
        return;
      }
      formFor === 'EDIT' ? handleEditDevice() : handleAddDevice();
    } catch (error) {
      setLoader(false);
    }
  };

  const fetchProviders = async () => {
    try {
      setLoader(true);
      await dispatch(getDeviceProvidersList({}));
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  useEffect(() => {
    fetchProviders();
  }, []);
  useEffect(() => {
    const formatedDeviceProviders =
      deviceProviders.map(data => ({ id: data?._id || '', name: data?.name })) || [];
    setDeviceProviderOptions(formatedDeviceProviders);
  }, [deviceProviders]);
  const getSelectedDeviceProviderOption = () => {
    try {
      const selectedDeviceProvider: IDefaultOption | null =
        deviceProviderOptions.find(
          data => (data?.id || data?._id) === deviceData?.provider_details?._id,
        ) || null;
      if (selectedDeviceProvider) {
        return selectedDeviceProvider;
      } else {
        return deviceData?.provider_details
          ? {
              id: deviceData?.provider_details?._id || '',
              name: deviceData?.provider_details?.name || '',
            }
          : null;
      }
    } catch (error) {}
  };
  return (
    <div className="flex flex-wrap max-w-[500px] w-[500px] gap-y-4 overflow-hidden">
      <div className="flex items-center w-full">
        <div className="flex items-center flex-1">
          <label className="pl-2 text-xl font-semibold text-black2">{title}</label>
        </div>
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => dispatch(setModal(initModal))}
        >
          <CrossIcon height={32} width={32} />
        </div>
      </div>
      <div className="flex flex-wrap w-full max-h-[calc(100vh- 180px)] gap-y-1">
        <div className="w-full">
          <label className="font-medium text-xs text-black2 w-full">
            {t('workshopAppointment.deviceProvider')}
          </label>
          <Select
            options={[...deviceProviderOptions]}
            setSelected={val =>
              setDeviceData({
                ...deviceData,
                provider_details: {
                  ...deviceData?.provider_details,
                  name: val?.name,
                  _id: val?._id || val?.id,
                },
              })
            }
            selected={getSelectedDeviceProviderOption() || null}
            placeholder={t('workshopAppointment.deviceProviderPlaceholder')}
            error={error?.deviceProvider}
          />
        </div>
        <div className="w-full">
          <label className="font-medium text-xs text-black2 w-full">
            {t('workshopAppointment.deviceProvider')}
          </label>
          <InputField
            onChange={data => setDeviceData({ ...deviceData, imeie: data })}
            error={error?.device}
            value={deviceData.imeie}
            placeholder="Enter device imei"
          />
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-x-4 mt-6">
        <PrimaryDarkButton
          label={
            formFor === 'EDIT'
              ? t('workshopAppointment.saveChanges')
              : t('workshopAppointment.addDevice')
          }
          onClick={() => handleSubmit()}
          width="w-1/2"
        />
        <DarkButton
          label={t('CANCEL')}
          width="w-1/2"
          onClick={() => dispatch(setModal(initModal))}
        />
      </div>
    </div>
  );
};

export default AddEditDevice;
